<template>
  <div>
    <!-- Connection status dot (hidden while call UI is visible) -->
    <div v-show="!visible" class="wr-signal-dot" :class="signalClass" :title="signalTitle"></div>

    <transition name="call-fade">
      <div v-if="visible" class="ic-overlay">
        <div class="ic-popup">

          <div class="ic-brand">
            <img src="/images/myroom_logo.png" alt="" class="ic-logo" />
            <span class="ic-brand-text">MyRoom</span>
          </div>

          <h1 class="ic-title">Incoming lesson</h1>
          <p class="ic-subtitle">English lesson with {{ tutorFirstName }}</p>

          <div class="ic-avatar-wrap">
            <div class="ic-pulse"></div>
            <div class="ic-pulse ic-pulse-2"></div>
            <div class="ic-pulse ic-pulse-3"></div>
            <div class="ic-avatar-glow"></div>
            <img
              v-if="displayTutorPhoto"
              :src="displayTutorPhoto"
              class="ic-avatar"
              :alt="tutorFirstName"
              @error="onTutorPhotoError"
            />
            <div v-else class="ic-avatar ic-avatar-fallback">
              {{ tutorInitials }}
            </div>
            <div class="ic-tutor-badge">
              <span class="ic-badge-name">{{ tutorFirstName }}</span>
              <span class="ic-online-dot" aria-hidden="true"></span>
              <span class="ic-tutor-pill">Tutor</span>
            </div>
          </div>

          <p class="ic-message">
            {{ tutorFirstName }} is calling you for your lesson.<br>
            Answer to join the lesson.
          </p>

          <div class="ic-actions">
            <div class="ic-action-item">
              <button type="button" class="ic-circle-btn ic-circle-decline" @click="decline" aria-label="Decline">
                <svg class="ic-icon-decline" width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
              </button>
              <span class="ic-action-label">Decline</span>
            </div>
            <div class="ic-action-item">
              <button type="button" class="ic-circle-btn ic-circle-answer" @click="enter" aria-label="Answer">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                </svg>
              </button>
              <span class="ic-action-label">Answer</span>
            </div>
          </div>

          <p class="ic-countdown">Auto-dismisses in {{ countdown }}s</p>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'IncomingCallComponent',

  props: {
    userId:       { type: [String, Number], required: true },
    token:        { type: String, required: true },
    signalingUrl: { type: String, default: '' },
    // Where "Answer" navigates. NetEnglish overrides this with
    // /webRTC?roomid={scheduleItemId} since /lesson-room is MyPage-only.
    lessonUrlTemplate: { type: String, default: '/lesson-room/{scheduleItemId}' },
  },

  data() {
    return {
      connState:      'connecting',
      socket:         null,
      visible:        false,
      scheduleItemId: null,
      tutorName:      '',
      tutorPhoto:     '',
      photoLoadFailed: false,
      lessonTime:     '',
      countdown:      60,
      countdownTimer: null,
      ringtone:       null,
    };
  },

  computed: {
    tutorFirstName() {
      const raw = (this.tutorName || '').replace(/^Teacher\s+/i, '').trim();
      if (!raw) return 'Your Tutor';
      return raw.split(/\s+/)[0] || raw;
    },
    tutorInitials() {
      const parts = (this.tutorName || '?').replace(/^Teacher\s+/i, '').trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return (parts[0] || '?').slice(0, 2).toUpperCase();
    },
    displayTutorPhoto() {
      if (this.photoLoadFailed || !this.tutorPhoto) return '';
      return this.tutorPhoto;
    },
    signalClass() {
      return {
        'dot-connecting': this.connState === 'connecting',
        'dot-connected':  this.connState === 'connected',
        'dot-error':      this.connState === 'error',
      };
    },
    signalTitle() {
      return {
        connecting: 'Connecting to lesson server…',
        connected:  'Connected — ready to receive calls',
        error:      'Cannot connect to lesson server',
      }[this.connState] || '';
    },
  },

  mounted() {
    this.ringtone = new Audio('/mp3/incoming-call-v2.mp3');
    this.ringtone.loop = true;

    const STORAGE_KEY = 'myroom_audio_unlocked';

    this._unlockAudio = () => {
      if (this._audioUnlocked || sessionStorage.getItem(STORAGE_KEY)) {
        this._audioUnlocked = true;
        return;
      }
      const silent = new Audio(
        'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA'
      );
      silent.play().then(() => {
        this._audioUnlocked = true;
        sessionStorage.setItem(STORAGE_KEY, '1');
      }).catch(() => {});
    };

    if (!sessionStorage.getItem(STORAGE_KEY)) {
      document.addEventListener('click', this._unlockAudio, { once: true });
      document.addEventListener('keydown', this._unlockAudio, { once: true });
      document.addEventListener('touchstart', this._unlockAudio, { once: true, passive: true });
    } else {
      this._audioUnlocked = true;
    }

    this.connect();
  },

  beforeDestroy() {
    if (this._unlockAudio) {
      document.removeEventListener('click', this._unlockAudio);
      document.removeEventListener('keydown', this._unlockAudio);
      document.removeEventListener('touchstart', this._unlockAudio);
    }
    this.stopRingtone();
    clearInterval(this.countdownTimer);
    if (this.socket) this.socket.disconnect();
  },

  methods: {

    normalizeTutorPhotoUrl(url) {
      if (!url || typeof url !== 'string') return '';
      const trimmed = url.trim();
      if (!trimmed) return '';
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      if (trimmed.startsWith('//')) return `${window.location.protocol}${trimmed}`;
      if (trimmed.startsWith('/')) return `${window.location.origin}${trimmed}`;
      return `${window.location.origin}/${trimmed.replace(/^\/+/, '')}`;
    },

    onTutorPhotoError() {
      console.warn('[IncomingCall] tutor photo failed to load:', this.tutorPhoto);
      this.photoLoadFailed = true;
    },

    connect() {
      const url = this.signalingUrl;
      if (!url) {
        console.warn('[IncomingCall] signalingUrl is empty — incoming calls disabled');
        this.connState = 'error';
        return;
      }

      if (!window.io) {
        console.warn('[IncomingCall] window.io not loaded yet — loading Socket.IO from signaling server');
        const s = document.createElement('script');
        s.src = url + '/socket.io/socket.io.js?v=2';
        s.onload = () => this.connect();
        s.onerror = () => {
          console.error('[IncomingCall] failed to load socket.io from signaling server');
          this.connState = 'error';
        };
        document.head.appendChild(s);
        return;
      }

      console.log(`[IncomingCall] connecting to ${url} as user ${this.userId}`);

      this.socket = window.io(url, {
        reconnection:        true,
        reconnectionAttempts: Infinity,
        reconnectionDelay:   1000,
        reconnectionDelayMax: 5000,
        transports: ['websocket', 'polling'],
      });

      this.socket.on('connect', () => {
        this.connState = 'connected';
        console.log(`[IncomingCall] connected (${this.socket.id}), registering user ${this.userId}`);
        this.register();
      });

      this.socket.on('disconnect', (reason) => {
        this.connState = 'connecting';
        console.log(`[IncomingCall] disconnected: ${reason}`);
      });

      this.socket.on('connect_error', (err) => {
        this.connState = 'error';
        console.error(`[IncomingCall] connection error: ${err.message}`);
      });

      this.socket.on('reconnect', () => {
        this.connState = 'connected';
        console.log('[IncomingCall] reconnected, re-registering');
        this.register();
      });

      this.socket.on('auth-error', ({ message }) => {
        console.error('[IncomingCall] auth error:', message);
      });

      this.socket.on('incoming-call', (data) => {
        console.log('[IncomingCall] incoming-call received:', data);
        this.showCall(data);
      });

      this.socket.on('call-cancelled', ({ scheduleItemId }) => {
        console.log('[IncomingCall] call-cancelled received for room', scheduleItemId);
        if (this.visible && String(scheduleItemId) === String(this.scheduleItemId)) {
          this.dismiss();
        }
      });
    },

    register() {
      this.socket.emit('register-user', {
        userId: this.userId,
        token:  this.token,
      });
    },

    showCall({ scheduleItemId, tutorName, tutorPhoto, lessonTime }) {
      if (this.visible) {
        console.log('[IncomingCall] ignoring duplicate incoming-call (popup already visible)');
        return;
      }
      this.scheduleItemId = scheduleItemId;
      this.tutorName      = tutorName  || '';
      this.tutorPhoto     = this.normalizeTutorPhotoUrl(tutorPhoto);
      this.photoLoadFailed = false;
      this.lessonTime     = lessonTime || '';
      this.countdown      = 60;
      this.visible        = true;

      console.log('[IncomingCall] showing call UI — tutor:', this.tutorName, 'photo:', this.tutorPhoto || '(none)');

      this.ringtone.currentTime = 0;
      this.ringtone.play().catch(e => {
        console.warn('[IncomingCall] ringtone autoplay blocked:', e.message);
      });

      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) this.decline();
      }, 1000);
    },

    enter() {
      console.log('[IncomingCall] student accepted, navigating to lesson room', this.scheduleItemId);
      this.socket.emit('call-accepted', {
        scheduleItemId: this.scheduleItemId,
        studentUserId:  this.userId,
      });
      this.dismiss();
      window.location.href = this.lessonUrlTemplate.replace('{scheduleItemId}', this.scheduleItemId);
    },

    decline() {
      console.log('[IncomingCall] student declined');
      this.socket.emit('call-declined', {
        scheduleItemId: this.scheduleItemId,
        studentUserId:  this.userId,
      });
      this.dismiss();
    },

    dismiss() {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
      this.visible = false;
      this.stopRingtone();
    },

    stopRingtone() {
      if (this.ringtone) {
        this.ringtone.pause();
        this.ringtone.currentTime = 0;
      }
    },
  },
};
</script>

<style scoped>
/* ── Signal status dot ─────────────────────────────────────────────────────── */
.wr-signal-dot {
  position: fixed;
  bottom: 12px;
  right: 12px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  z-index: 9000;
  cursor: default;
  transition: background 0.3s;
}
.dot-connecting { background: #f0a500; animation: dot-blink 1.2s ease-in-out infinite; }
.dot-connected  { background: #4caf50; }
.dot-error      { background: #e53935; }
@keyframes dot-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

/* ── Shared incoming call UI ───────────────────────────────────────────────── */
.ic-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.ic-popup {
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.ic-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}
.ic-logo {
  height: 32px;
  width: auto;
  object-fit: contain;
  display: block;
}
.ic-brand-text {
  font-size: 26px;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.3px;
  line-height: 1;
}

.ic-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px;
  line-height: 1.2;
}

.ic-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 28px;
}

/* Avatar */
.ic-avatar-wrap {
  position: relative;
  width: 180px;
  height: 180px;
  margin-bottom: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Ringing pulse rings */
.ic-pulse {
  position: absolute;
  width: 148px;
  height: 148px;
  border-radius: 50%;
  border: 2px solid rgba(58, 133, 208, 0.55);
  animation: ic-ring-pulse 2s ease-out infinite;
  opacity: 0;
  pointer-events: none;
}
.ic-pulse-2 { animation-delay: 0.65s; }
.ic-pulse-3 { animation-delay: 1.3s; }

@keyframes ic-ring-pulse {
  0% {
    transform: scale(1);
    opacity: 0.65;
  }
  70% {
    opacity: 0.15;
  }
  100% {
    transform: scale(1.55);
    opacity: 0;
  }
}

.ic-avatar-glow {
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(58, 133, 208, 0.18) 0%, rgba(58, 133, 208, 0) 70%);
  pointer-events: none;
}

.ic-avatar {
  width: 148px;
  height: 148px;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  border: 4px solid #fff;
  box-shadow: 0 4px 24px rgba(58, 133, 208, 0.22);
  position: relative;
  z-index: 1;
  background: #e8eef5;
  animation: ic-avatar-ring 2s ease-in-out infinite;
}

@keyframes ic-avatar-ring {
  0%, 100% { box-shadow: 0 4px 24px rgba(58, 133, 208, 0.22); }
  50%      { box-shadow: 0 4px 32px rgba(58, 133, 208, 0.42); }
}

.ic-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3a85d0, #2563a8);
  color: #fff;
  font-size: 42px;
  font-weight: 700;
  letter-spacing: 1px;
}

.ic-tutor-badge {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #1a1a1a;
  color: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.ic-badge-name {
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ic-online-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}

.ic-tutor-pill {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
}

.ic-message {
  font-size: 15px;
  line-height: 1.55;
  color: #6b7280;
  margin: 0 0 32px;
}

/* Actions */
.ic-actions {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 40px;
  margin-bottom: 24px;
}

.ic-action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.ic-circle-btn {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: transform 0.12s ease, opacity 0.12s ease;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
}
.ic-circle-btn:hover  { opacity: 0.92; }
.ic-circle-btn:active { transform: scale(0.96); }

.ic-circle-decline { background: #ef4444; }
.ic-circle-answer  { background: #22c55e; }

.ic-icon-decline {
  transform: rotate(135deg);
}

.ic-action-label {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
}

.ic-countdown {
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
}

/* ── Mobile: full-screen white ─────────────────────────────────────────────── */
@media (max-width: 767px) {
  .ic-overlay {
    background: #fff;
    padding: 32px 24px;
  }

  .ic-popup {
    max-width: 100%;
  }

  .ic-title {
    font-size: 26px;
  }

  .ic-avatar-wrap {
    width: 160px;
    height: 160px;
    margin-bottom: 32px;
  }

  .ic-pulse {
    width: 128px;
    height: 128px;
  }

  .ic-avatar {
    width: 128px;
    height: 128px;
  }

  .ic-actions {
    gap: 48px;
  }
}

/* ── Desktop: centered modal + blurred backdrop ────────────────────────────── */
@media (min-width: 768px) {
  .ic-overlay {
    background: rgba(15, 23, 42, 0.38);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }

  .ic-popup {
    background: #fff;
    border-radius: 24px;
    padding: 40px 36px 32px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);
  }

  .ic-action-item {
    flex-direction: row;
    gap: 14px;
  }

  .ic-actions {
    gap: 28px;
  }
}

/* Fade animation */
.call-fade-enter-active,
.call-fade-leave-active {
  transition: opacity 0.25s ease;
}
.call-fade-enter-active .ic-popup,
.call-fade-leave-active .ic-popup {
  transition: transform 0.25s ease;
}
.call-fade-enter,
.call-fade-leave-to {
  opacity: 0;
}
.call-fade-enter .ic-popup,
.call-fade-leave-to .ic-popup {
  transform: scale(0.96);
}
</style>
